// /account — money, in one place. Two cards: the personal agent plan (with this week's usage and
// the choice of which plan the agent draws from) and the team's seats (Standard and Premium run the
// agent, a Memory seat is the plugin with shared spaces). They belong together: the meter switch on
// the first card only exists because of a seat bought on the second, and splitting them across two
// tabs is what made the shell confusing. Nothing about installing lives here — that is Connections.
// Standalone, outside the analytics shell, which keeps /settings and /billing at the root. Signed
// out it offers the same Sign in as Connections — a full-page sign-in that comes straight back here
// (authConfig.js signInWithRedirect), so no one is bounced through the analytics-branded /login.
// A personal account (Gmail, iCloud, Hide My Email: an org of one) gets no Team section, since team
// plans group people by work domain and could never reach anyone from there, just a line pointing
// at team plans. It does get its shared spaces and Stripe's portal for card and invoices. Without
// `account` from /me/plans (an older backend) the page is shown as before.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Box, Button, Typography } from "@mui/material";
import { signInWithRedirect, cachedAccount, accountEmail } from "../authConfig";
import MemoryNav from "../components/MemoryNav";
import MemoryPlanCard from "../components/MemoryPlanCard";
import TeamPlanCard from "../components/TeamPlanCard";
import { C, CARD, MICRO_LABEL, SectionHeader } from "../components/memoryTheme";
import { getPlans, openBillingPortal } from "../utils/api";
import { AGENT_SITE } from "../utils/landing";

// "1 of 1 shared space on Free" or "No limit on your plan", always with the per-space cap.
function SharingCard({ sharing }) {
  if (!sharing) return null;
  const { shared_spaces: n = 0, free_limit: limit = 1, paid, max_members: cap = 6 } = sharing;
  const full = !paid && n >= limit;
  return (
    <Box sx={{ ...CARD, p: 2, mt: 2.5 }} data-testid="sharing-card">
      <Typography sx={MICRO_LABEL}>Shared spaces</Typography>
      <Typography sx={{ fontSize: 14, color: C.ink, fontWeight: 600, mt: 0.5 }}>
        {paid ? `${n} shared · no limit on your plan` : `${n} of ${limit} shared space${limit === 1 ? "" : "s"} on Free`}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.5, lineHeight: 1.6 }}>
        Up to {cap} people in each, you included. Everyone's chats run on their own plan.
        {full ? " Upgrade to share more spaces." : ""}{" "}
        <Box component="a" href="/memory" sx={{ color: C.tealDeep }}>Manage spaces</Box>
      </Typography>
    </Box>
  );
}

export default function Account() {
  const { instance } = useMsal();
  const isAuth = useIsAuthenticated();
  const navigate = useNavigate();
  const signedInAs = accountEmail(cachedAccount(instance));
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);   // { account, sharing } from /me/plans, when the backend sends them
  const [summaryRead, setSummaryRead] = useState(false);   // the Team section waits for it, so it never flashes
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    document.title = "Account · Assertion AI Studio";
    return () => { document.title = "Assertion AI Studio"; };
  }, []);

  useEffect(() => {
    if (!isAuth) return;
    getPlans().then((d) => setSummary(d?.account ? { account: d.account, sharing: d.sharing } : null))
      .catch(() => setSummary(null)).finally(() => setSummaryRead(true));
  }, [isAuth]);
  // Personal and on no team: no Team section. Unknown (older backend): shown as before.
  const showTeam = !summary || !summary.account.personal || summary.account.on_team;
  const portal = async () => {
    setError(""); setPortalBusy(true);
    try { await openBillingPortal("/account"); }
    catch (e) { setError(e?.response?.data?.error || e?.message || "Could not open billing."); setPortalBusy(false); }
  };

  const signIn = async () => {
    setError("");
    try {
      await signInWithRedirect(instance);   // full page, back here after (see authConfig.js)
    } catch (e) {
      setError(e?.message || "Sign-in failed. Please try again.");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#0b0b0f" }}>
      <MemoryNav />
      {!isAuth ? (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                   justifyContent: "center", gap: 2, color: "#fff" }}>
          <Typography sx={{ opacity: 0.7 }}>Sign in to see your plan and usage.</Typography>
          <Button variant="contained" onClick={signIn}>Sign in</Button>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto", bgcolor: C.canvas }}>
          <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
            {/* Who this is, and the way out. /logout does the whole sign-out: clears the admin hint
                and any view-as scope, and ends the sign-in site's session too. */}
            <Box sx={{ ...CARD, px: 2, py: 1.5, mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={MICRO_LABEL}>Signed in as</Typography>
                <Typography sx={{ fontSize: 14, color: C.ink, mt: 0.25, overflowWrap: "anywhere" }}>
                  {signedInAs || "your Assertion account"}
                </Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={() => navigate("/logout")}
                      sx={{ textTransform: "none", color: C.body, borderColor: C.line, "&:hover": { borderColor: C.muted } }}>
                Sign out
              </Button>
            </Box>

            <SectionHeader title="Your plan" />
            <MemoryPlanCard />

            {summary?.account?.has_billing && (
              <Box sx={{ ...CARD, px: 2, py: 1.5, mt: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={MICRO_LABEL}>Payment and invoices</Typography>
                  <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25 }}>Change your card or download receipts, on Stripe.</Typography>
                </Box>
                <Button variant="outlined" size="small" onClick={portal} disabled={portalBusy}
                        sx={{ textTransform: "none", color: C.body, borderColor: C.line, "&:hover": { borderColor: C.muted } }}>
                  {portalBusy ? "Opening…" : "Open billing ↗"}
                </Button>
              </Box>
            )}

            <SharingCard sharing={summary?.sharing} />

            {!summaryRead ? null : showTeam ? (
              <>
                <Typography sx={{ ...MICRO_LABEL, mt: 3, mb: 1 }}>Team</Typography>
                <TeamPlanCard />
              </>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 3 }} data-testid="team-plans-link">
                Using Assertion at work?{" "}
                <Box component="a" href={AGENT_SITE} sx={{ color: C.tealDeep }}>See team plans</Box>
              </Typography>
            )}
            {error && <Typography sx={{ fontSize: 12.5, color: "#b42318", mt: 1.5 }}>{error}</Typography>}
          </Box>
        </Box>
      )}
    </Box>
  );
}
